class KootaMegaNavigation extends HTMLElement {
  connectedCallback() {
    this.menuDetails = Array.from(this.querySelectorAll('.koota-mega'));
    this.onToggle = this.handleToggle.bind(this);
    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);

    this.menuDetails.forEach((details) => details.addEventListener('toggle', this.onToggle));
    document.addEventListener('pointerdown', this.onPointerDown);
    document.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    this.menuDetails.forEach((details) => details.removeEventListener('toggle', this.onToggle));
    document.removeEventListener('pointerdown', this.onPointerDown);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  handleToggle(event) {
    const currentDetails = event.currentTarget;
    const currentSummary = currentDetails.querySelector('summary');
    currentSummary?.setAttribute('aria-expanded', String(currentDetails.open));

    queueMicrotask(() => {
      const headerWrapper = this.closest('.header-wrapper');
      if (headerWrapper) headerWrapper.preventHide = this.menuDetails.some((details) => details.open);
    });

    if (!currentDetails.open) return;

    this.menuDetails.forEach((details) => {
      if (details === currentDetails || !details.open) return;
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  handlePointerDown(event) {
    if (!this.contains(event.target)) this.closeAll();
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    const openDetails = this.menuDetails.find((details) => details.open);
    if (!openDetails) return;

    const summary = openDetails.querySelector('summary');
    this.closeAll();
    summary?.focus();
  }

  closeAll() {
    this.menuDetails.forEach((details) => {
      if (!details.open) return;
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }
}

if (!customElements.get('koota-mega-navigation')) {
  customElements.define('koota-mega-navigation', KootaMegaNavigation);
}

class KootaMenuDrawer extends HTMLElement {
  connectedCallback() {
    this.rootDetails = this.querySelector(':scope > details');
    this.rootSummary = this.rootDetails?.querySelector(':scope > summary');
    this.panel = this.querySelector('.koota-drawer__panel');
    this.closeButtons = Array.from(this.querySelectorAll('[data-koota-drawer-close]'));
    this.groups = Array.from(this.querySelectorAll('.koota-drawer__group'));
    this.headerSection = this.closest('.section-header');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktopMedia = window.matchMedia('(min-width: 990px)');
    this.isClosing = false;

    if (!this.rootDetails || !this.rootSummary || !this.panel) return;

    this.onRootSummaryClick = this.handleRootSummaryClick.bind(this);
    this.onRootToggle = this.handleRootToggle.bind(this);
    this.onCloseClick = this.handleCloseClick.bind(this);
    this.onGroupToggle = this.handleGroupToggle.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onBreakpointChange = this.handleBreakpointChange.bind(this);

    this.rootSummary.addEventListener('click', this.onRootSummaryClick);
    this.rootDetails.addEventListener('toggle', this.onRootToggle);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.onCloseClick));
    this.groups.forEach((details) => details.addEventListener('toggle', this.onGroupToggle));
    this.addEventListener('keydown', this.onKeyDown);
    this.desktopMedia.addEventListener('change', this.onBreakpointChange);

    if (this.rootDetails.open) this.openDrawer();
  }

  disconnectedCallback() {
    if (!this.rootDetails) return;

    this.rootSummary.removeEventListener('click', this.onRootSummaryClick);
    this.rootDetails.removeEventListener('toggle', this.onRootToggle);
    this.closeButtons.forEach((button) => button.removeEventListener('click', this.onCloseClick));
    this.groups.forEach((details) => details.removeEventListener('toggle', this.onGroupToggle));
    this.removeEventListener('keydown', this.onKeyDown);
    this.desktopMedia.removeEventListener('change', this.onBreakpointChange);
    window.clearTimeout(this.closeTimer);
    this.rootDetails.classList.remove('is-opening', 'menu-opening');
    document.body.classList.remove('overflow-hidden-tablet');
    this.headerSection?.classList.remove('menu-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
  }

  handleRootSummaryClick(event) {
    if (!this.rootDetails.open) return;
    event.preventDefault();
    this.closeDrawer();
  }

  handleRootToggle() {
    this.rootSummary.setAttribute('aria-expanded', String(this.rootDetails.open));

    if (this.rootDetails.open && !this.isClosing) {
      this.openDrawer();
    } else if (!this.rootDetails.open) {
      this.finishClose();
    }
  }

  openDrawer() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.body.classList.add('overflow-hidden-tablet');
    this.headerSection?.classList.add('menu-open');

    window.requestAnimationFrame(() => {
      this.rootDetails.classList.add('is-opening', 'menu-opening');
      if (typeof trapFocus === 'function') trapFocus(this.rootDetails, this.querySelector('.koota-drawer__close'));
    });
  }

  handleCloseClick(event) {
    event.preventDefault();
    this.closeDrawer();
  }

  closeDrawer({ restoreFocus = true } = {}) {
    if (!this.rootDetails.open || this.isClosing) return;

    this.isClosing = true;
    this.restoreFocusAfterClose = restoreFocus;
    this.rootDetails.classList.remove('is-opening', 'menu-opening');

    const delay = this.reducedMotion.matches ? 0 : 280;
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      this.rootDetails.removeAttribute('open');
      this.finishClose();
    }, delay);
  }

  finishClose() {
    window.clearTimeout(this.closeTimer);
    this.rootDetails.classList.remove('is-opening', 'menu-opening');
    this.groups.forEach((details) => {
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
    document.body.classList.remove('overflow-hidden-tablet');
    this.headerSection?.classList.remove('menu-open');

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.restoreFocusAfterClose === false ? null : this.rootSummary);
    if (this.restoreFocusAfterClose !== false) this.rootSummary.focus();

    this.restoreFocusAfterClose = true;
    this.isClosing = false;
  }

  handleGroupToggle(event) {
    const currentDetails = event.currentTarget;
    currentDetails.querySelector('summary')?.setAttribute('aria-expanded', String(currentDetails.open));

    if (!currentDetails.open) return;

    this.groups.forEach((details) => {
      if (details === currentDetails || !details.open) return;
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    const openGroup = this.groups.find((details) => details.open && details.contains(event.target));
    if (openGroup) {
      event.preventDefault();
      const summary = openGroup.querySelector('summary');
      openGroup.removeAttribute('open');
      summary?.setAttribute('aria-expanded', 'false');
      summary?.focus();
      return;
    }

    event.preventDefault();
    this.closeDrawer();
  }

  handleBreakpointChange(event) {
    if (event.matches) this.closeDrawer({ restoreFocus: false });
  }
}

if (!customElements.get('koota-menu-drawer')) {
  customElements.define('koota-menu-drawer', KootaMenuDrawer);
}
